// Live vehicles: poll /api/vehicles and hold each vehicle's latest fix, with the
// leg it travelled to get there ready for a glide that nothing draws yet (#176).
//
// The GL feature building that used to live here is gone: the circle draws marks
// from the model (SPEC-redesign §4.3, §4.4), so this module is polling and
// nothing else. It still carries the ARITHMETIC of a glide, but nothing reaches
// it: see `vehiclePositions()` and `poll()` below. S1 owns it from its merge on.
import { positionAt, type Tween } from '../shared/tween.ts';

export type Vehicle = {
  id: string;
  line: string;
  type: 'tram' | 'bus';
  lat: number;
  lon: number;
  ts: number;
  bearing: number | null;
  headsign: string | null;
  delay: number | null;
  nextStop: string | null;
};
export type Snapshot = { updatedAt: number; feedTs: number; vehicles: Vehicle[] };

type Track = Tween & { v: Vehicle };

const POLL_MS = 10_000;

/**
 * What the vehicle feed knows about itself. One fact: whether the request to
 * our own server failed. It is the one case that reads „brak połączenia z
 * serwerem", and it means what it says.
 *
 * The feed's own state is not here. `src/data/data.ts` settles it from
 * `/api/health`, which is the server's own reading (ADR-0007) rather than a
 * guess from how old a snapshot is, and a second answer from this module was
 * dead output and a third poller of `/api/health` (#153).
 */
export type VehicleHealth = { offline: boolean };

/**
 * The live tracks: each vehicle's latest fix, and the one before it.
 *
 * The circle asks for the latest fix and nothing else (#176). `from` and `t0`
 * are the far end and the start of a leg NOTHING computes from today — they are
 * kept so the glide of Decision 44 (#96) has a seam to obey.
 */
const tracks = new Map<string, Track>();

/**
 * Read where it is used, never once at load: a resident who turns reduced motion
 * on mid-session must get it on the next READING, not on the next reload (spec
 * §8, #153). „On the next frame" would be wrong — the one reader left is
 * `poll()`, so the next reading is the next poll that answers, not the next
 * paint. `src/wheel/wheel.ts` reads the same query per paint, and the query is a
 * cached object in the browser: asking it is a property read.
 *
 * Since #176 this read has no computing consumer at all. `vehiclePositions()`
 * does not ask, and the one `positionAt` left is an identity for BOTH branches
 * (see `poll()`), so the answer cannot change a number anywhere. It is kept,
 * and kept live rather than hoisted, because it is the seam the glide of
 * Decision 44 (#96) has to obey the moment it exists — and because a module-
 * scope read is precisely the bug #153 fixed. What reduced motion still governs
 * today is `src/wheel/wheel.ts` and `src/screens/intro.ts`.
 *
 * `src/data/vehicles.test.ts` pins this by COUNTING how often the browser is asked,
 * which is the only thing left that can distinguish a live read from a hoisted
 * one. That pin belongs to this call: a later cleanup that removes the
 * `positionAt` line in `poll()` should delete it in the same commit, not repair
 * it — it would be failing for a cleanup, not a regression.
 *
 * Reading it here rather than at module scope is also what makes this module,
 * and so the whole seam above it, importable under `node:test` without a shim.
 */
const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Where each vehicle is right now: the latest fix the feed has given us.
 *
 * It does NOT sample the glide, and that is the fix for #176. There is one
 * caller — `announce()` in `src/data/data.ts` — and it runs in the SAME turn as the
 * poll that just wrote `t0 = performance.now()`, so `positionAt` was asked at
 * k ≈ 0. That does not answer the previous fix: it answers
 * `from + ε·(to − from)`, a point just off it that the feed never reported at
 * all. Nothing animates the marks (`src/wheel/wheel.ts`'s rAF re-projects the lat/lon
 * it already holds), so no later frame ever advanced that glide. Measured on
 * the built app against the live feed: of 1324 readings of on-screen vehicles
 * whose fix had just moved, ZERO stood at any fix the feed had ever reported —
 * and the rail's distances with them, because both are computed from this one
 * answer (`Item.position`).
 *
 * The tween is not removed: `poll()` still records where a mark stood, so a rAF
 * consumer can glide from it the day Decision 44 (#96) brings one. Until then
 * the honest answer to „where is it" is the newest thing we know.
 */
export function vehiclePositions(): Map<string, [number, number]> {
  return new Map([...tracks].map(([id, t]) => [id, t.to]));
}

export function startVehicles(onSnapshot: (s: Snapshot, health: VehicleHealth) => void) {
  let last: Snapshot | null = null;

  /** Consecutive polls of /api/vehicles that failed. One miss on a ten-second
   * cadence is a blip; two in a row is a connection the resident has lost. */
  let failedPolls = 0;

  /**
   * A poll that did not come back. The first one leaves the pill alone — with a
   * snapshot on screen it keeps ageing, and with none it keeps "łączę się…" —
   * so a single dropped request never claims the server is gone.
   */
  function reportFailure(e: unknown, where: string) {
    console.warn(`vehicles: ${where}`, e);
    if (++failedPolls < 2) return;
    onSnapshot(last ?? { updatedAt: 0, feedTs: 0, vehicles: [] }, { offline: true });
  }

  async function poll() {
    try {
      const res = await fetch('/api/vehicles', { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const snap = (await res.json()) as Snapshot;
      const now = performance.now();
      const still = reduced();
      const seen = new Set<string>();
      for (const v of snap.vehicles) {
        seen.add(v.id);
        const cur = tracks.get(v.id);
        const to: [number, number] = [v.lon, v.lat];
        // `from` is where the mark stood when this fix arrived, kept as the far
        // end of a leg nothing computes from yet.
        //
        // Say it plainly: this `positionAt` is an IDENTITY today. In the browser
        // `now - cur.t0 ≈ POLL_MS = TWEEN_MS`, so k clamps to 1, the ease is 1,
        // and BOTH branches of `reduced` return `cur.to` — the line is
        // `from: cur.to` written the long way. It is not interpolation, and no
        // reader should think it is. Consequently `Track.from`, `Track.t0` and
        // the live `reduced()` read have no consumer that computes anything from
        // them until an animator exists (Decision 44 / #96); the call is kept
        // because that animator has to enter through here, and because under a
        // rAF consumer a leg CAN be cut short, where the identity stops holding.
        //
        // It is an identity in the browser, not under `t.mock.timers`, which
        // fakes `setInterval` but not `performance.now()`.
        if (!cur) tracks.set(v.id, { v, from: to, to, t0: now });
        else tracks.set(v.id, { v, from: positionAt(cur, now, still), to, t0: now });
      }
      for (const id of tracks.keys()) if (!seen.has(id)) tracks.delete(id);
      last = snap;
      failedPolls = 0;
      onSnapshot(snap, { offline: false });
    } catch (e) {
      reportFailure(e, 'poll failed');
    }
  }

  // poll() reports its own failures through onSnapshot; a rejection escaping it
  // would be a bug in that path, so it is reported the same way rather than left
  // as an unhandled rejection
  const tick = () => {
    poll().catch((e: unknown) => {
      reportFailure(e, 'poll rejected past its own catch');
    });
  };
  tick();
  setInterval(tick, POLL_MS);
}
